import { Link, useParams } from 'react-router-dom'
import type { CSSProperties } from 'react'
import { useChurch, useChurchEvents } from '../hooks/useChurches'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Button } from '../components/ui/Button'
import { getEventType } from '../components/ui/eventTypes'

/** Strip a trailing " Church" so e.g. "Emmaus Baptist Church" → "Emmaus Baptist". */
function shortName(name: string) {
  return name.replace(/\s+church$/i, '').trim() || name
}

/** Format a church's location as "City, ST" (2-letter uppercase state), or null
 *  when there is no city or no clean 2-letter state/region code to present. */
function cityStateLabel(city: string | null, state: string | null): string | null {
  const c = city?.trim()
  const s = state?.trim()
  if (!c) return null
  const code = s && /^[A-Za-z]{2}$/.test(s) ? s.toUpperCase() : null
  if (!code) return null
  return `${c}, ${code}`
}

// Illustrative mock community-post cards on the Welcome page. Names, quotes,
// times, and comment counts are MOCK (see docs/onboarding-wiring-todo.md #1);
// the card *location* is wired to the invited church's real city/state via
// cityStateLabel() + neighborMeta() below (dropped entirely when unavailable —
// never another church's city).
const NEIGHBOR_CARDS: Array<{
  initials: string
  name: string
  group: string
  withCity: boolean
  prefix?: string
  tint: string
  ink: string
  q: string
  foot: string
  rotate: string
}> = [
  {
    initials: 'KM',
    name: 'Kayla M.',
    group: 'Community group',
    withCity: true,
    tint: 'var(--event-social-tint)',
    ink: 'var(--event-social)',
    q: '"Just moved here with two littles — looking for a kid-friendly church that isn\'t too strict. Any recommendations?"',
    foot: '3h ago · 14 comments',
    rotate: '-1.4deg',
  },
  {
    initials: 'DT',
    name: 'Devon T.',
    group: 'Neighbors group',
    withCity: false,
    prefix: 'New in town',
    tint: 'var(--event-hike-tint)',
    ink: 'var(--event-hike)',
    q: '"Where do people go for hikes around here? Would love to actually meet some folks and not just stay home."',
    foot: 'yesterday · 9 comments',
    rotate: '0.8deg',
  },
  {
    initials: 'PR',
    name: 'Priya R.',
    group: 'Community group',
    withCity: true,
    tint: 'var(--event-study-tint)',
    ink: 'var(--event-study)',
    q: '"Is there a Bible study that actually welcomes newcomers? Don\'t know anyone here yet and could use the company."',
    foot: '2d ago · 21 comments',
    rotate: '-0.6deg',
  },
]

/** Build a card's meta line: real "City, ST" (when available) + an optional
 *  prefix + the generic group descriptor. */
function neighborMeta(card: (typeof NEIGHBOR_CARDS)[number], cityState: string | null): string {
  const parts: string[] = []
  if (card.withCity && cityState) parts.push(cityState)
  if (card.prefix) parts.push(card.prefix)
  parts.push(card.group)
  return parts.join(' · ')
}

type PopupRow = { color: string; name: string; day: string }

// Map info-window fallback rows — shown only when the church has no real
// upcoming events to source (see docs/onboarding-wiring-todo.md #2/#3).
const PLACEHOLDER_POPUP_ROWS: PopupRow[] = [
  { color: 'var(--event-study)', name: 'VBS week', day: 'starts Mon' },
  { color: 'var(--event-potluck)', name: 'Fellowship lunch', day: 'Sat' },
]

/** Short label for an occurrence date: weekday ("Sat") within the next week,
 *  else month + day ("Jul 1"). */
function shortDayLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diffDays = Math.floor((d.getTime() - Date.now()) / 86_400_000)
  return diffDays >= 0 && diffDays < 7
    ? d.toLocaleDateString('en-US', { weekday: 'short' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const eyebrow = (color: string): CSSProperties => ({
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color,
})

// Decorative map pins (event-colored teardrops scattered on the map's right half).
const DECO_PINS: Array<{ top: string; right: string; color: string }> = [
  { top: '20%', right: '28%', color: 'var(--event-potluck)' },
  { top: '52%', right: '6%', color: 'var(--event-study)' },
  { top: '86%', right: '40%', color: 'var(--event-sports)' },
  { top: '30%', right: '7%', color: 'var(--event-social)' },
]

// Four How-it-works steps. Screenshots are placeholder slots — see
// docs/onboarding-wiring-todo.md (#6).
const STEPS = [
  {
    eyebrow: 'Step 01 · Add a gathering',
    title: 'Post an event in a couple of minutes',
    body: 'Title, time, place, and a line about what to bring. Mark it kid-friendly, dog-friendly, newcomer-welcome — the details that help someone decide to show up.',
    checks: ['Recurring events set up once', 'Add a photo or use a category cover'],
    url: 'app.fellowship.org/admin/events/new',
    shot: 'Create / Edit event screen',
    imageFirst: false,
  },
  {
    eyebrow: 'Step 02 · Bring what you have',
    title: 'Import from Google Calendar in one click',
    body: 'Already keep a calendar? Connect it and your recurring gatherings flow in automatically — nothing to retype, and it stays in sync going forward.',
    checks: ['Set up in about two minutes', 'Pick which calendars to share'],
    url: 'app.fellowship.org/admin/import',
    shot: 'Google Calendar import screen',
    imageFirst: true,
  },
  {
    eyebrow: 'Step 03 · Welcome them in',
    title: "See who's coming and organize the details",
    body: "Turn on RSVPs to get a headcount, run dish sign-ups so the potluck isn't all desserts, and send a quick note to everyone who's planning to come.",
    checks: ['Dish & volunteer sign-ups', 'Message attendees in one tap'],
    url: 'app.fellowship.org/admin/events/rsvps',
    shot: 'RSVP / attendee list screen',
    imageFirst: false,
  },
  {
    eyebrow: 'Step 04 · Watch it grow',
    title: "See how many neighbors you're reaching",
    body: (short: string) =>
      `A simple dashboard shows how many people are finding ${short} on Fellowship each week — which gatherings draw the most interest, and how many are new faces.`,
    checks: ['Views, RSVPs, and follows over time', 'No spreadsheets, no guesswork'],
    url: 'app.fellowship.org/admin/dashboard',
    shot: 'Traffic / reach dashboard screen',
    imageFirst: true,
  },
] as const

function BrowserFrame({ url, label }: { url: string; label: string }) {
  return (
    <div
      className="onb-step-media"
      style={{
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: 'var(--shadow-lifted)',
        border: '1px solid var(--line)',
        background: 'var(--surface)',
      }}
    >
      <div
        style={{
          height: 40,
          background: 'var(--bg-alt)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '0 16px',
        }}
      >
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#ff5f57' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#febc2e' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#28c840' }} />
        <div
          style={{
            marginLeft: 12,
            maxWidth: 320,
            flex: 1,
            height: 24,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            fontSize: 11.5,
            color: 'var(--muted)',
            fontFamily: 'var(--font-mono)',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {url}
        </div>
      </div>
      {/* TODO(wiring): replace this placeholder slot with a real 16:10 admin
          screenshot (<img>). See docs/onboarding-wiring-todo.md (#6). */}
      <div
        style={{
          width: '100%',
          aspectRatio: '16 / 10',
          background:
            'repeating-linear-gradient(45deg,#efe7da,#efe7da 16px,#e7dccb 16px,#e7dccb 32px)',
          display: 'flex',
          alignItems: 'flex-end',
          padding: 14,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--muted)',
            background: 'rgba(250,246,240,.9)',
            padding: '4px 9px',
            borderRadius: 6,
          }}
        >
          {label}
        </span>
      </div>
    </div>
  )
}

export default function ChurchWelcomePage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: churchData, isLoading } = useChurch(slug)
  const { data: eventsData } = useChurchEvents(slug)
  const church = churchData?.data

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[13px] text-muted">Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  if (!church) {
    return (
      <PublicLayout>
        <div
          className="flex flex-col items-center justify-center gap-4 px-6 text-center"
          style={{ height: 'calc(100vh - 80px)' }}
        >
          <p className="text-[15px] text-ink-2">We could not find that church listing.</p>
          <Link to="/for-churches" className="text-primary text-[13px] no-underline font-medium">
            Learn about Fellowship for churches →
          </Link>
        </div>
      </PublicLayout>
    )
  }

  if (church.claimed) {
    return (
      <PublicLayout>
        <div
          className="flex flex-col items-center justify-center gap-4 px-6 text-center"
          style={{ height: 'calc(100vh - 80px)' }}
        >
          <div className="text-[40px]">🏛️</div>
          <p className="serif text-[22px] m-0">{church.name} is already managed on Fellowship.</p>
          <p className="text-[14px] text-ink-2 m-0">
            This listing has already been claimed by an admin.
          </p>
          <Link
            to={`/churches/${church.slug}`}
            className="text-primary text-sm no-underline font-medium"
          >
            View their profile →
          </Link>
        </div>
      </PublicLayout>
    )
  }

  const churchName = church.name
  const churchShort = shortName(church.name)
  // Real, presentable location for the mock neighbor cards (null → city dropped).
  const neighborCity = cityStateLabel(church.city, church.state)

  // Map info-window: real upcoming events when available, else placeholder copy.
  const upcomingOccurrences = (eventsData?.data ?? [])
    .filter((o) => !o.isCancelled)
    .slice()
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
  const realPopupRows: PopupRow[] = upcomingOccurrences.slice(0, 2).map((o) => ({
    color: getEventType(o.event.eventType ?? 'potluck').color,
    name: o.overrideTitle ?? o.event.title,
    day: shortDayLabel(o.startsAt),
  }))
  const hasRealEvents = realPopupRows.length > 0
  const popupRows = hasRealEvents ? realPopupRows : PLACEHOLDER_POPUP_ROWS
  const popupCount = hasRealEvents
    ? church.upcomingEventCount > 0
      ? church.upcomingEventCount
      : upcomingOccurrences.length
    : 3
  const popupBadge = `${popupCount} event${popupCount === 1 ? '' : 's'}`

  const claimPath = `/churches/${church.id}/claim`
  const scrollToHow = () => {
    const el = document.getElementById('how-it-works')
    if (el)
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 16,
        behavior: 'smooth',
      })
  }

  return (
    <PublicLayout>
      <div style={{ background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-sans)' }}>
        {/* ── 1. Hero — full-bleed brand glow extends past the centered column ── */}
        <section className="onb-hero">
          {/* Decorative glow lives on the full-width hero wrapper (not the 1120px
              column) so it reads as a page background bleeding to the edges
              instead of a circle clipped inside the column. */}
          <div className="onb-hero-glow" aria-hidden />
          <div
            className="onb onb-pad"
            style={{ position: 'relative', zIndex: 1, padding: '64px 56px 44px' }}
          >
            <div style={{ position: 'relative', maxWidth: 740 }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  ...eyebrow('var(--brand-accent-deep)'),
                  background: 'var(--brand-gradient-calm)',
                  padding: '7px 14px',
                  borderRadius: 999,
                  marginBottom: 24,
                }}
              >
                A personal invitation for {churchName}
              </div>
              <h1
                className="onb-h1"
                style={{
                  fontSize: 64,
                  lineHeight: 1.03,
                  letterSpacing: '-0.035em',
                  fontWeight: 700,
                  margin: '0 0 22px',
                }}
              >
                Someone nearby is looking for{' '}
                <span
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontStyle: 'italic',
                    fontWeight: 500,
                    color: 'var(--brand-accent)',
                  }}
                >
                  exactly you.
                </span>
              </h1>
              <p
                style={{
                  fontSize: 19,
                  lineHeight: 1.6,
                  color: 'var(--ink-2)',
                  margin: '0 0 32px',
                  maxWidth: 620,
                }}
              >
                Every week, people new to your town ask the same question online:{' '}
                <em>where do we belong?</em> Fellowship is where they look — and we've already
                listed {churchShort} so they can find you. Claim your page to make it yours.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <Button size="lg" to={claimPath}>
                  Claim your page&nbsp;&nbsp;→
                </Button>
                <Button variant="ghost" size="lg" onClick={scrollToHow}>
                  See how it works
                </Button>
              </div>
            </div>
          </div>
        </section>

        <div className="onb">
          {/* ── 2. The neighbors are already asking ─────────────────────────── */}
          <section className="onb-pad" style={{ padding: '8px 56px 28px' }}>
            <div style={{ ...eyebrow('var(--muted)'), marginBottom: 10 }}>
              This is happening in your town every week
            </div>
            <h2
              className="onb-h2"
              style={{
                fontSize: 30,
                fontWeight: 700,
                letterSpacing: '-0.025em',
                margin: '0 0 30px',
                maxWidth: 620,
              }}
            >
              The neighbors are already asking. Your church is the answer.
            </h2>
            {/* TODO(wiring): names, quotes, times, and comment counts on these
              cards are static mock copy from the design handoff (the card
              location is wired to the real church). See
              docs/onboarding-wiring-todo.md (#1). */}
            <div className="onb-grid-3" style={{ gap: 18 }}>
              {NEIGHBOR_CARDS.map((c) => (
                <div
                  key={c.initials}
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-soft)',
                    padding: 22,
                    transform: `rotate(${c.rotate})`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        background: c.tint,
                        color: c.ink,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        flex: '0 0 auto',
                      }}
                    >
                      {c.initials}
                    </span>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.3 }}>
                      <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{c.name}</span>
                      <br />
                      {neighborMeta(c, neighborCity)}
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: 14.5,
                      lineHeight: 1.55,
                      color: 'var(--ink)',
                      margin: '0 0 12px',
                    }}
                  >
                    {c.q}
                  </p>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.foot}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ── 3. Map section — don't be the blank spot ────────────────────── */}
          <section className="onb-pad" style={{ padding: '0 56px 60px' }}>
            <div
              style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-lifted)',
                minHeight: 440,
                background: 'var(--bg-alt)',
                border: '1px solid var(--line)',
              }}
            >
              {/* TODO(wiring): static map image — replace with a live map render
                centered on the church. See docs/onboarding-wiring-todo.md (#5). */}
              <img
                src="/assets/discover-map.png"
                alt=""
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: '62% 50%',
                  opacity: 0.55,
                  filter: 'saturate(.85)',
                }}
              />
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(100deg, rgba(250,246,240,.97) 0%, rgba(250,246,240,.93) 30%, rgba(250,246,240,.55) 55%, rgba(250,246,240,.12) 78%, rgba(250,246,240,0) 100%)',
                }}
              />
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(0deg, rgba(31,26,46,.10), rgba(31,26,46,0) 40%)',
                }}
              />

              {/* TODO(wiring): "180 churches nearby" is a placeholder count — needs a
                real nearby-church count. See docs/onboarding-wiring-todo.md (#4). */}
              <div
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  boxShadow: 'var(--shadow-soft)',
                  borderRadius: 999,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--ink)',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: 'var(--event-hike)',
                    animation: 'onb-pulse 2s infinite',
                  }}
                />
                180 churches nearby
              </div>

              {/* Decorative teardrop pins */}
              {DECO_PINS.map((p, i) => (
                <div
                  key={i}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: p.top,
                    right: p.right,
                    transform: 'translate(-50%,-100%)',
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      background: p.color,
                      boxShadow: 'var(--shadow-pin)',
                      border: '2px solid #fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#fff',
                        transform: 'rotate(45deg)',
                      }}
                    />
                  </div>
                </div>
              ))}

              {/* The "you" pin + info-window popup */}
              <div
                style={{
                  position: 'absolute',
                  top: '66%',
                  left: '73%',
                  transform: 'translate(-50%,-100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: 14,
                    width: 46,
                    height: 46,
                    borderRadius: 999,
                    background: 'var(--brand-primary)',
                    animation: 'onb-pinring 2.4s ease-out infinite',
                    transform: 'translate(-50%,-50%)',
                  }}
                />
                {/* Event names + count are wired to the church's real upcoming
                  events; only the no-events fallback copy is placeholder. See
                  docs/onboarding-wiring-todo.md (#2, #3). */}
                <div
                  style={{
                    position: 'relative',
                    marginBottom: 11,
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    boxShadow: 'var(--shadow-lifted)',
                    borderRadius: 14,
                    padding: '11px 13px',
                    minWidth: 214,
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      marginBottom: 9,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: 'var(--ink)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {churchShort}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--brand-accent-deep)',
                        background: 'var(--brand-gradient-calm)',
                        padding: '3px 9px',
                        borderRadius: 999,
                        flex: '0 0 auto',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {popupBadge}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {popupRows.map((row, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12.5,
                          color: 'var(--ink)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: row.color,
                            flex: '0 0 auto',
                          }}
                        />
                        <span style={{ fontWeight: 600 }}>{row.name}</span>
                        <span style={{ color: 'var(--muted)', marginLeft: 'auto' }}>{row.day}</span>
                      </div>
                    ))}
                  </div>
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: '50%',
                      bottom: -7,
                      width: 12,
                      height: 12,
                      background: 'var(--surface)',
                      borderRight: '1px solid var(--line)',
                      borderBottom: '1px solid var(--line)',
                      transform: 'translateX(-50%) rotate(45deg)',
                    }}
                  />
                </div>
                <div
                  style={{
                    position: 'relative',
                    width: 42,
                    height: 42,
                    margin: '0 auto',
                    borderRadius: '50% 50% 50% 0',
                    transform: 'rotate(-45deg)',
                    background: 'var(--brand-gradient)',
                    boxShadow: 'var(--shadow-brand)',
                    border: '2.5px solid #fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: '50%',
                      background: '#fff',
                      transform: 'rotate(45deg)',
                    }}
                  />
                </div>
              </div>

              {/* Copy block */}
              <div style={{ position: 'relative', padding: 48, maxWidth: 560 }}>
                <div style={{ ...eyebrow('var(--brand-accent-deep)'), marginBottom: 14 }}>
                  When neighbors open the map
                </div>
                <h2
                  className="onb-h2"
                  style={{
                    fontSize: 38,
                    lineHeight: 1.08,
                    letterSpacing: '-0.03em',
                    fontWeight: 700,
                    margin: '0 0 16px',
                  }}
                >
                  Don't be the blank spot on the map.
                </h2>
                <p
                  style={{
                    fontSize: 17,
                    lineHeight: 1.6,
                    color: 'var(--ink-2)',
                    margin: '0 0 26px',
                    maxWidth: 440,
                  }}
                >
                  People open Fellowship and search for what's near them this weekend. Every pin is
                  a church already showing up. Claim your page and {churchShort} appears right where
                  they're looking — instead of being the gap between everyone else.
                </p>
                <Button size="lg" to={claimPath}>
                  Put {churchShort} on the map&nbsp;&nbsp;→
                </Button>
              </div>
            </div>
          </section>

          {/* ── 4. Why Fellowship exists ────────────────────────────────────── */}
          <section className="onb-pad" style={{ padding: '8px 56px 60px' }}>
            <div style={{ ...eyebrow('var(--muted)'), marginBottom: 28 }}>
              Why Fellowship exists
            </div>
            <div className="onb-grid-3" style={{ gap: 36 }}>
              {[
                [
                  'One feed, every denomination',
                  'No one should have to guess which doors are open. We gather gatherings from every tradition into one warm place.',
                ],
                [
                  'Found, not just searched',
                  'The people most looking for community are the ones least likely to know your name. Fellowship helps them stumble into you.',
                ],
                [
                  'Come as you are',
                  'Kids welcome, dogs welcome, latecomers welcome. Real gatherings for real people, exactly where they are.',
                ],
              ].map(([title, body]) => (
                <div key={title}>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      marginBottom: 10,
                    }}
                  >
                    {title}
                  </div>
                  <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0 }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* ── 5. How it works ─────────────────────────────────────────────── */}
          <section id="how-it-works" className="onb-pad" style={{ padding: '8px 56px 24px' }}>
            <div style={{ ...eyebrow('var(--muted)'), marginBottom: 10 }}>How it works</div>
            <h2
              className="onb-h2"
              style={{
                fontSize: 34,
                fontWeight: 700,
                letterSpacing: '-0.025em',
                margin: '0 0 12px',
                maxWidth: 640,
              }}
            >
              Claim it once. Run everything from one simple place.
            </h2>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.6,
                color: 'var(--ink-2)',
                margin: '0 0 48px',
                maxWidth: 560,
              }}
            >
              No new website to build, no training required. Here's what managing {churchShort} on
              Fellowship actually looks like.
            </p>

            {STEPS.map((step, idx) => {
              const textBlock = (
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '.05em',
                      textTransform: 'uppercase',
                      color: 'var(--brand-accent-deep)',
                      marginBottom: 12,
                    }}
                  >
                    {step.eyebrow}
                  </div>
                  <h3
                    style={{
                      fontSize: 25,
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      margin: '0 0 12px',
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontSize: 16,
                      lineHeight: 1.6,
                      color: 'var(--ink-2)',
                      margin: '0 0 16px',
                    }}
                  >
                    {typeof step.body === 'function' ? step.body(churchShort) : step.body}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {step.checks.map((c) => (
                      <div
                        key={c}
                        style={{ display: 'flex', gap: 10, fontSize: 14.5, color: 'var(--ink-2)' }}
                      >
                        <span style={{ color: 'var(--brand-primary)' }}>✓</span>
                        {c}
                      </div>
                    ))}
                  </div>
                </div>
              )
              const frame = <BrowserFrame url={step.url} label={`Drop: ${step.shot}`} />
              return (
                <div
                  key={step.eyebrow}
                  className={`onb-step ${step.imageFirst ? 'onb-step--image-first' : 'onb-step--text-first'}`}
                  style={{ marginBottom: idx === STEPS.length - 1 ? 8 : 56 }}
                >
                  {step.imageFirst ? (
                    <>
                      {frame}
                      {textBlock}
                    </>
                  ) : (
                    <>
                      {textBlock}
                      {frame}
                    </>
                  )}
                </div>
              )
            })}
          </section>

          {/* ── 6. Closing CTA (dark) ───────────────────────────────────────── */}
          <section
            style={{
              position: 'relative',
              overflow: 'hidden',
              background: '#0e1020',
              padding: '60px 56px',
              textAlign: 'center',
            }}
          >
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: -120,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 620,
                height: 420,
                background:
                  'radial-gradient(circle at 50% 50%, rgba(124,92,211,.45), rgba(59,76,202,.18) 45%, transparent 70%)',
                filter: 'blur(10px)',
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative' }}>
              <h2
                className="onb-h2"
                style={{
                  fontSize: 38,
                  fontWeight: 700,
                  letterSpacing: '-0.025em',
                  color: '#fff',
                  margin: '0 0 14px',
                }}
              >
                Be the welcome they're{' '}
                <span
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontStyle: 'italic',
                    fontWeight: 500,
                    color: '#c3b6f0',
                  }}
                >
                  looking for
                </span>
                .
              </h2>
              <p style={{ fontSize: 17, color: 'rgba(255,255,255,.7)', margin: '0 0 28px' }}>
                Free to claim. By claiming you confirm you represent {churchShort}.
              </p>
              <Button size="lg" to={claimPath}>
                Claim {churchShort}&nbsp;&nbsp;→
              </Button>
            </div>
          </section>
        </div>
      </div>
    </PublicLayout>
  )
}
